/**
 * BalanceCard — 내 잔액 조회 결과 카드
 *
 * 메일링크 마이페이지 잔액 패널과 동일한 항목/순서로 표시:
 *   캐시 / 포인트 / 마일리지(적립·사용) / 쿠폰(메일·SMS·팩스·LMS·MMS)
 *
 * 발송 가능 예상 건수는 표시하지 않는다 — 자세한 정보는 마이페이지에서 안내.
 */

import { cn } from '@/lib/utils';
import type { BalanceData, BalanceItem } from '@/types/api';

interface BalanceCardProps {
  data: BalanceData;
}

export function BalanceCard({ data }: BalanceCardProps) {
  return (
    <div className="ml-mt-2 ml-flex ml-flex-col ml-gap-3">
      {/* 캐시·포인트·마일리지 섹션 */}
      <div
        className={cn(
          'ml-rounded-cb-md ml-border ml-border-line ml-bg-surface',
          'ml-px-3 ml-py-2.5 ml-flex ml-flex-col ml-gap-1.5'
        )}
      >
        <Row label="메일링크 캐시" item={data.cash} />
        <Row label="메일링크 포인트" item={data.point} />
        <Row label="적립가능 마일리지" item={data.mileage.earnable} />
        <Row label="사용가능 마일리지" item={data.mileage.usable} />
      </div>

      {/* 쿠폰 섹션 */}
      <div
        className={cn(
          'ml-rounded-cb-md ml-border ml-border-line ml-bg-surface',
          'ml-px-3 ml-py-2.5 ml-flex ml-flex-col ml-gap-1.5'
        )}
      >
        <Row label="메일 쿠폰" item={data.coupons.email} accent />
        <Row label="SMS 쿠폰" item={data.coupons.sms} accent />
        <Row label="팩스 쿠폰" item={data.coupons.fax} accent />
        <Row label="LMS 쿠폰" item={data.coupons.lms} accent />
        <Row label="MMS 쿠폰" item={data.coupons.mms} accent />
      </div>

      {/* 안내문 — 마이페이지로 유도 */}
      <div className="ml-text-cb-sm ml-text-mute ml-leading-relaxed">
        자세한 발송 가능 건수는 상단{' '}
        <span className="ml-font-semibold ml-text-ink">[잔액확인]</span>{' '}
        버튼을 눌러 확인하세요.
      </div>
    </div>
  );
}

interface RowProps {
  label: string;
  item: BalanceItem;
  /** 쿠폰류는 brand 색으로 강조 (마이페이지 blue_c 클래스와 동일 톤) */
  accent?: boolean;
}

function Row({ label, item, accent = false }: RowProps) {
  return (
    <div className="ml-flex ml-items-baseline ml-justify-between ml-gap-3">
      <span className="ml-text-cb-sm ml-text-mute">{label}</span>
      <span
        className={cn(
          'ml-text-cb-base ml-font-semibold',
          accent ? 'ml-text-brand-600' : 'ml-text-ink'
        )}
      >
        {item.formatted}
      </span>
    </div>
  );
}
